import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { apiClient, type Transaction, type TransactionFilters, type TransactionListResponse } from '../utils/api';
import { TransactionForm } from './TransactionForm';

const columnHelper = createColumnHelper<Transaction>();

export function TransactionTable() {
  const [data, setData] = useState<TransactionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    limit: 50,
    sort: 'desc',
  });

  // Quick period filters
  const [quickPeriod, setQuickPeriod] = useState<string>('');

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedIds.has(row.original.id!)}
            onChange={() => {
              const newSelected = new Set(selectedIds);
              if (newSelected.has(row.original.id!)) {
                newSelected.delete(row.original.id!);
              } else {
                newSelected.add(row.original.id!);
              }
              setSelectedIds(newSelected);
            }}
          />
        ),
        size: 50,
      }),
      columnHelper.accessor('id', {
        header: 'ID',
        cell: ({ getValue }) => (
          <button
            onClick={() => navigator.clipboard.writeText(String(getValue()))}
            className="text-telegram-link hover:underline"
            title="Нажмите, чтобы скопировать"
          >
            {getValue()}
          </button>
        ),
        size: 80,
      }),
      columnHelper.accessor('name', {
        header: 'Название',
        cell: ({ getValue }) => {
          const value = getValue();
          return value.length > 30 ? (
            <span title={value}>
              {value.substring(0, 30)}...
            </span>
          ) : value;
        },
        size: 200,
      }),
      columnHelper.accessor('money', {
        header: 'Сумма',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isNegative = value < 0;
          return (
            <span className={isNegative ? 'text-red-500' : 'text-green-500'}>
              {isNegative ? '-' : '+'}
              {Math.abs(value).toFixed(2)} {row.original.currency}
            </span>
          );
        },
        size: 120,
      }),
      columnHelper.accessor('fee', {
        header: 'Комиссия',
        cell: ({ getValue, row }) => `${getValue().toFixed(2)} ${row.original.currency}`,
        size: 100,
      }),
      columnHelper.accessor('account', {
        header: 'Счет',
        size: 120,
      }),
      columnHelper.accessor('executor', {
        header: 'Исполнитель',
        size: 120,
      }),
      columnHelper.accessor('category', {
        header: 'Категория',
        size: 120,
      }),
      columnHelper.accessor('transaction_time', {
        header: 'Время транзакции',
        cell: ({ getValue }) => dayjs(getValue()).format('DD.MM.YYYY HH:mm'),
        size: 150,
      }),
      columnHelper.accessor('payment', {
        header: 'Платеж',
        cell: ({ getValue }) => {
          const value = getValue();
          const isUrl = value.startsWith('http://') || value.startsWith('https://');
          
          if (isUrl) {
            return (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-telegram-link hover:underline"
              >
                Ссылка
              </a>
            );
          }
          
          return value.length > 20 ? (
            <span title={value}>
              {value.substring(0, 20)}...
            </span>
          ) : value;
        },
        size: 100,
      }),
      columnHelper.accessor('note', {
        header: 'Примечание',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '-';
          
          if (value.length > 30) {
            return (
              <button
                onClick={() => alert(value)}
                className="text-telegram-link hover:underline"
                title="Нажмите для просмотра полного текста"
              >
                {value.substring(0, 30)}...
              </button>
            );
          }
          
          return value;
        },
        size: 150,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Действия',
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              onClick={() => setEditingTransaction(row.original)}
              className="text-telegram-link hover:underline text-sm"
            >
              Изменить
            </button>
            <button
              onClick={() => handleDelete(row.original.id!)}
              className="text-red-500 hover:underline text-sm"
            >
              Удалить
            </button>
          </div>
        ),
        size: 120,
      }),
    ],
    [selectedIds]
  );

  const table = useReactTable({
    data: data?.transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const result = await apiClient.getTransactions(filters);
      setData(result);
    } catch (error) {
      console.error('Error loading transactions:', error);
      alert(`Ошибка загрузки: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Удалить эту транзакцию?')) return;
    
    try {
      await apiClient.deleteTransaction(id);
      await loadTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Ошибка удаления: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Удалить ${selectedIds.size} транзакций?`)) return;
    
    try {
      await Promise.all(Array.from(selectedIds).map(id => apiClient.deleteTransaction(id)));
      setSelectedIds(new Set());
      await loadTransactions();
    } catch (error) {
      console.error('Error bulk deleting transactions:', error);
      alert(`Ошибка удаления: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  };

  const handleQuickPeriod = (period: string) => {
    setQuickPeriod(period);
    const now = dayjs();
    let start: string;
    let end: string = now.format('YYYY-MM-DD');

    switch (period) {
      case 'week':
        start = now.subtract(7, 'day').format('YYYY-MM-DD');
        break;
      case 'month':
        start = now.subtract(1, 'month').format('YYYY-MM-DD');
        break;
      case 'quarter':
        start = now.subtract(3, 'month').format('YYYY-MM-DD');
        break;
      case 'year':
        start = now.subtract(1, 'year').format('YYYY-MM-DD');
        break;
      default:
        start = '';
        end = '';
    }

    setFilters(prev => ({
      ...prev,
      start: start ? `${start}T00:00:00` : undefined,
      end: end ? `${end}T23:59:59` : undefined,
      page: 1,
    }));
  };

  const exportToCSV = () => {
    if (!data?.transactions.length) return;

    const headers = ['ID', 'Название', 'Сумма', 'Комиссия', 'Валюта', 'Счет', 'Исполнитель', 'Категория', 'Время транзакции', 'Время поступления', 'Платеж', 'Примечание'];
    const rows = data.transactions.map(t => [
      t.id,
      t.name,
      t.money,
      t.fee,
      t.currency,
      t.account,
      t.executor,
      t.category,
      t.transaction_time,
      t.destination_time || '',
      t.payment,
      t.note || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  if (editingTransaction) {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => setEditingTransaction(null)}
            className="text-telegram-link"
          >
            ← Назад к таблице
          </button>
        </div>
        <TransactionForm
          transaction={editingTransaction}
          onSuccess={() => {
            setEditingTransaction(null);
            loadTransactions();
          }}
          onCancel={() => setEditingTransaction(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-telegram-secondary-bg rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-telegram-text">Фильтры</h3>
        
        {/* Quick period filters */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleQuickPeriod('')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              quickPeriod === '' ? 'bg-telegram-button text-telegram-button-text' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Все
          </button>
          <button
            onClick={() => handleQuickPeriod('week')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              quickPeriod === 'week' ? 'bg-telegram-button text-telegram-button-text' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Неделя
          </button>
          <button
            onClick={() => handleQuickPeriod('month')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              quickPeriod === 'month' ? 'bg-telegram-button text-telegram-button-text' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Месяц
          </button>
          <button
            onClick={() => handleQuickPeriod('quarter')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              quickPeriod === 'quarter' ? 'bg-telegram-button text-telegram-button-text' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Квартал
          </button>
        </div>

        {/* Custom date range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-telegram-text mb-1">
              С даты
            </label>
            <input
              type="date"
              className="telegram-input"
              value={filters.start ? dayjs(filters.start).format('YYYY-MM-DD') : ''}
              onChange={(e) => {
                setQuickPeriod('custom');
                setFilters(prev => ({
                  ...prev,
                  start: e.target.value ? `${e.target.value}T00:00:00` : undefined,
                  page: 1,
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-telegram-text mb-1">
              До даты
            </label>
            <input
              type="date"
              className="telegram-input"
              value={filters.end ? dayjs(filters.end).format('YYYY-MM-DD') : ''}
              onChange={(e) => {
                setQuickPeriod('custom');
                setFilters(prev => ({
                  ...prev,
                  end: e.target.value ? `${e.target.value}T23:59:59` : undefined,
                  page: 1,
                }));
              }}
            />
          </div>
        </div>

        {/* Search and filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Поиск по названию"
            className="telegram-input"
            value={filters.name || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value || undefined, page: 1 }))}
          />
          <input
            type="text"
            placeholder="Фильтр по категории"
            className="telegram-input"
            value={filters.category || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value || undefined, page: 1 }))}
          />
          <input
            type="text"
            placeholder="Фильтр по исполнителю"
            className="telegram-input"
            value={filters.executor || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, executor: e.target.value || undefined, page: 1 }))}
          />
        </div>
      </div>


      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            className="px-4 py-2 bg-red-500 text-white rounded-lg"
          >
            Удалить выбранные ({selectedIds.size})
          </button>
        )}
        <button
          onClick={exportToCSV}
          disabled={!data?.transactions.length}
          className="px-4 py-2 bg-telegram-button text-telegram-button-text rounded-lg disabled:opacity-50"
        >
          Экспорт CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-telegram-secondary-bg rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-telegram-hint">
            Загрузка...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-telegram-button text-telegram-button-text">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-sm font-medium"
                        style={{ width: header.getSize() }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 text-sm text-telegram-text">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-telegram-hint">
            Страница {data.meta.page} из {data.meta.totalPages} (всего: {data.meta.total})
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page! - 1) }))}
              disabled={data.meta.page === 1}
              className="px-3 py-1 bg-telegram-button text-telegram-button-text rounded disabled:opacity-50"
            >
              Назад
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: Math.min(data.meta.totalPages, prev.page! + 1) }))}
              disabled={data.meta.page === data.meta.totalPages}
              className="px-3 py-1 bg-telegram-button text-telegram-button-text rounded disabled:opacity-50"
            >
              Вперед
            </button>
          </div>
        </div>
      )}
    </div>
  );
}